import { isStrongPassword } from '@dadohr/shared/domainTypes/password';
import styled from '@emotion/styled';
import { TextField, Typography } from '@material-ui/core';
import { useState } from 'react';
import { Form } from '../../../../components/Form';
import { FormRow } from '../../../../components/Form/FormRow';
import { FormSubmitButton } from '../../../../components/Form/FormSubmitButton';
import { PageLoader } from '../../../../components/Loader';
import { SomethingWentWrong } from '../../../../components/SomethingWentWrong';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../i18next';
import { Centered } from '../../../../layout/Centered';
import { Page } from '../../../../layout/Page';
import { PageBody } from '../../../../layout/PageBody';
import { Section, SectionTitle } from '../../../../layout/Section';
import { TopNavBarLogoOnly } from '../../../../layout/TopNav';
import { useRoutes } from '../../../../routes';
import {
  callFirebaseFunction,
  useFirebaseFunction
} from '../../../../services/firebaseFunctions';
import { WEAK_PASSWORD_SNACK } from '../../../../services/password';

const Wrapper = styled('div')`
  height: 100vh;
  width: 100%;
`;

const extractInvitationId = (pathname: string) =>
  pathname.replace('/accept-invitation/', '');

const TextFieldWithButton = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: '1fr min-content',
  gridColumnGap: p.theme.spacing(1),
  paddingBottom: p.theme.spacing(1.5)
}));

export const PageAcceptInvitation = () => {
  const { getPath } = useRoutes();
  const invitationId = extractInvitationId(getPath());
  const { t } = useUnsafeTranslation();
  const {
    enqueueSnackbarSuccess,
    enqueueSnackbarError,
    enqueueSnackbar
  } = useSnackbar();
  const [d, loading, error] = useFirebaseFunction<{
    user: {
      firstName: string;
      middleName: string;
      lastName: string;
      preferredName: string;
    };
    org: {
      name: string;
      logoUrl: string;
    };
    ssoRequired: boolean;
  }>('adminUser-getInvitationData', { id: invitationId });
  const [step, setStep] = useState<'VERIFY_EMAIL' | 'SET_PASSWORD'>(
    'VERIFY_EMAIL'
  );
  const [state, setState] = useState<{
    email: string;
    password: string;
    confirmedPassword: string;
  }>({
    email: '',
    password: '',
    confirmedPassword: ''
  });
  return (
    <Wrapper>
      <TopNavBarLogoOnly
        additionalLogoUrl={d ? d.org.logoUrl : undefined}
        additionalLogoAlt={d ? d.org.name : undefined}
      />
      {loading && <PageLoader />}
      {error && error.message === 'INVITATION_NOT_FOUND' && (
        <Centered height={500}>
          This invitation doesn't exist (anymore).
        </Centered>
      )}
      {error && error.message !== 'INVITATION_NOT_FOUND' && (
        <SomethingWentWrong height={500} />
      )}
      {d && (
        <Page width="M">
          <PageBody>
            {step === 'VERIFY_EMAIL' && (
              <Section>
                <SectionTitle>
                  <Typography variant="h4" component="h4">
                    Hey {d.user.preferredName} - welcome to Dado!
                  </Typography>
                </SectionTitle>
                <p>
                  You've been invited to join {d.org.name} as an administrator!
                </p>
                <p>
                  Please verify your email address below to complete the setup
                  process. Hint: use the same email address where you received
                  this invite.
                </p>
                <Form
                  onSubmit={() => {
                    return callFirebaseFunction(
                      'adminUser-verifyInvitationEmailAddress',
                      {
                        id: invitationId,
                        email: state.email
                      }
                    ).then(
                      () => {
                        if (d.ssoRequired) {
                          window.location.href = `/?email=${encodeURIComponent(
                            state.email.toLowerCase()
                          )}`;
                          return;
                        }
                        enqueueSnackbarSuccess('Email address verified!');
                        setStep('SET_PASSWORD');
                      },
                      () =>
                        enqueueSnackbarError(
                          `We don't recognize this email address - please try again.`
                        )
                    );
                  }}
                >
                  {({ submitting }) => (
                    <TextFieldWithButton>
                      <TextField
                        label="Email"
                        value={state.email}
                        onChange={(ev) =>
                          setState((s) => ({ ...s, email: ev.target.value }))
                        }
                        autoFocus
                        variant="outlined"
                        fullWidth
                      />
                      <FormSubmitButton
                        variant="contained"
                        color="primary"
                        type="submit"
                        submitting={submitting}
                        submitComponent="Verifying..."
                        disabled={!state.email}
                      >
                        Verify
                      </FormSubmitButton>
                    </TextFieldWithButton>
                  )}
                </Form>
              </Section>
            )}
            {step === 'SET_PASSWORD' && (
              <Section>
                <p>Thanks for verifying your email address!</p>
                <p>
                  Now please set a password. Afterwards you'll be able to login
                  to your Dado administrator account using this password and the
                  email address <strong>{state.email}</strong>.
                </p>

                <Form
                  onSubmit={async () => {
                    try {
                      const isPasswordStrongEnough = isStrongPassword(
                        state.password
                      );
                      if (!isPasswordStrongEnough) {
                        enqueueSnackbar(
                          WEAK_PASSWORD_SNACK.message(t),
                          WEAK_PASSWORD_SNACK.options
                        );
                        return;
                      }
                      console.log('onCreate', state);
                      await callFirebaseFunction<{
                        userId: string;
                      }>('adminUser-finishInviationProcess', {
                        invitationId,
                        email: state.email,
                        password: state.password
                      });
                      (window as any).location = '/home?source=invitation';
                    } catch (err: any) {
                      console.log(err);
                      if (err.code === 'auth/weak-password') {
                        enqueueSnackbarError(err.message);
                      }
                      if (err.code === 'auth/email-already-in-use') {
                        enqueueSnackbarError('Email address already in use!');
                      }
                      enqueueSnackbarError('Something went wrong!');
                    }
                  }}
                >
                  {({ submitting }) => (
                    <>
                      <FormRow>
                        <TextField
                          label="Password"
                          type="password"
                          autoFocus
                          value={state.password}
                          onChange={(ev) =>
                            setState((s) => ({
                              ...s,
                              password: ev.target.value
                            }))
                          }
                          variant="outlined"
                          fullWidth
                        />
                      </FormRow>
                      <FormRow>
                        <TextField
                          label="Confirm Password"
                          type="password"
                          value={state.confirmedPassword}
                          onChange={(ev) =>
                            setState((s) => ({
                              ...s,
                              confirmedPassword: ev.target.value
                            }))
                          }
                          variant="outlined"
                          fullWidth
                        />
                      </FormRow>
                      <FormRow>
                        <FormSubmitButton
                          fullWidth
                          variant="contained"
                          color="primary"
                          type="submit"
                          submitting={submitting}
                          submitComponent="Creating..."
                          disabled={
                            !state.password ||
                            !state.confirmedPassword ||
                            state.password !== state.confirmedPassword
                          }
                        >
                          Create account
                        </FormSubmitButton>
                      </FormRow>
                    </>
                  )}
                </Form>
              </Section>
            )}
          </PageBody>
        </Page>
      )}
    </Wrapper>
  );
};

export const AcceptInvitationPage = () => {};
